<template>
  <div>
    <!-- Totalizadores (topo) -->
    <b-row>
      <b-col
        v-for="card in totalizerCards"
        :key="card.key"
        cols="6"
        md="3"
        lg="2"
      >
        <b-card class="text-center totalizer-card">
          <h3 class="mb-0">
            {{ card.value }}
          </h3>
          <small class="text-body">{{ card.label }}</small>
        </b-card>
      </b-col>
    </b-row>

    <!-- Taxas -->
    <b-row>
      <b-col
        class="rate-col mb-1"
      >
        <b-card class="text-center h-100">
          <h2 class="text-primary mb-0">
            {{ rates.general_acceptance_rate }}%
          </h2>
          <small class="text-body">Taxa de aceitação geral</small>
        </b-card>
      </b-col>
      <b-col
        class="rate-col mb-1"
      >
        <b-card class="text-center h-100">
          <h2 class="text-success mb-0">
            {{ rates.strict_acceptance_rate }}%
          </h2>
          <small class="text-body">Taxa de aceitação sem editar</small>
        </b-card>
      </b-col>
      <b-col
        class="rate-col mb-1"
      >
        <b-card class="text-center h-100">
          <h2 class="text-info mb-0">
            {{ rates.edited_acceptance_rate }}%
          </h2>
          <small class="text-body">Taxa de aceitação com edição</small>
        </b-card>
      </b-col>
      <b-col
        class="rate-col mb-1"
      >
        <b-card class="text-center h-100">
          <h2 class="text-danger mb-0">
            {{ rates.rejection_rate }}%
          </h2>
          <small class="text-body">Taxa de rejeição</small>
        </b-card>
      </b-col>
      <b-col
        class="rate-col mb-1"
      >
        <b-card class="text-center h-100">
          <h2 class="mb-0">
            {{ avgProcessingLabel }}
          </h2>
          <small class="text-body">Tempo médio de processamento</small>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtro (abaixo) -->
    <b-card title="Filtros">
      <b-row>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <b-form-group
            label="Passo"
            label-for="searchStepOption"
          >
            <v-select
              id="searchStepOption"
              v-model="filters.step"
              :reduce="(option) => option.key"
              :options="stepOptions"
              :clearable="false"
              label="name"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <b-form-group
            label="Tipo"
            label-for="searchTypeOption"
          >
            <v-select
              id="searchTypeOption"
              v-model="filters.field_key"
              :reduce="(option) => option.value"
              :options="typeOptions"
              :clearable="false"
              label="label"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <b-form-group
            label="Período"
            label-for="searchRangeDate"
          >
            <flat-pickr
              id="searchRangeDate"
              v-model="rangeDate"
              class="form-control"
              :config="flatPickrConfig"
              placeholder="Selecione um período"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <b-form-group
            label="Parceiro de negócio (PN)"
            label-for="searchConsultant"
          >
            <v-select
              id="searchConsultant"
              v-model="filters.pn_consultant_id"
              multiple
              :reduce="(consultant) => consultant.id"
              :options="consultantList"
              :loading="loadingConsultants"
              :close-on-select="false"
              label="name"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <b-form-group
            label="Franquia digital"
            label-for="searchFranchise"
          >
            <v-select
              id="searchFranchise"
              v-model="filters.digital_franchise_id"
              multiple
              :reduce="(franchise) => franchise.id"
              :options="franchiseList"
              :loading="loadingFranchises"
              :close-on-select="false"
              label="name"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="d-flex align-items-end justify-content-end mb-1"
        >
          <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="loading"
            @click.prevent="clearFilters"
          >
            Limpar filtros
          </b-button>
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click.prevent="loadAll"
          >
            <b-spinner
              v-if="loading"
              small
            />
            <feather-icon
              v-else
              icon="SearchIcon"
              class="mr-50"
            />
            <span>{{ loading ? "Aguarde..." : "Pesquisar" }}</span>
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabela de auditoria -->
    <b-card title="Auditoria das recomendações">
      <b-table
        :items="auditItems"
        :fields="auditFields"
        :busy="loading"
        responsive
        show-empty
        empty-text="Nenhuma recomendação encontrada"
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(effectiveStatus(data.item))">
            {{ statusLabel(effectiveStatus(data.item)) }}
          </b-badge>
        </template>
      </b-table>
      <div
        v-if="auditMeta.total"
        class="mt-1"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Exibindo {{ auditMeta.from }} a {{ auditMeta.to }} de {{ auditMeta.total }} itens</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="auditMeta.current_page"
              :total-rows="auditMeta.total"
              :per-page="auditMeta.per_page"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="onPageChange"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BSpinner,
  BTable,
  BBadge,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import todayButton from '@/helpers/todayButton'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as sharedTypes from '@/modules/shared/store/types'
import * as types from '../store/types'

const STATUS_LABELS = {
  processing: 'Processando',
  presented: 'Apresentada',
  accepted: 'Aceita',
  accepted_edited: 'Aceita c/ edição',
  rejected: 'Rejeitada',
  regenerated: 'Regenerada',
  processing_error: 'Erro',
}

// Tradução do `field_key` (Tipo) salvo em inglês no banco → rótulo em PT.
const FIELD_TYPE_OPTIONS = [
  // Passo 1 — Poupar eficiente
  { value: 'saving_capacity', label: 'Capacidade de poupança' },
  { value: 'cut_expenses_oportunities', label: 'Corte de gastos' },
  { value: 'revenue_oportunities', label: 'Aumento de receitas' },
  // Passo 2 — Gestão financeira
  { value: 'using_customer_cash_flow', label: 'Faz fluxo de caixa' },
  { value: 'management_orientations', label: 'Orientações de gestão' },
  { value: 'retirement_age', label: 'Idade de aposentadoria' },
  // Passo 3 — Gestão de riscos
  { value: 'risk_protection', label: 'Gestão de riscos' },
  // Passo 4 — Garantia de renda futura
  { value: 'passo4/campo1', label: 'Montante da reserva necessária' },
  { value: 'passo4/campo2', label: 'Expectativa da reserva futura' },
  { value: 'retirement_income_advice', label: 'Renda na aposentadoria' },
  { value: 'pension_investment_advice', label: 'Previdência privada' },
  // Passo 5 — Reserva de curto prazo
  { value: 'reserve_months', label: 'Meses de reserva' },
  { value: 'passo5/campo2', label: 'Valor recomendado da reserva' },
  { value: 'allocation_orientations', label: 'Reserva de emergência' },
  // Passo 7 — Gestão de ativos
  { value: 'investor_profile', label: 'Perfil de investidor' },
  { value: 'asset_orientations', label: 'Gestão de ativos' },
  // Passo 8 — Planejamento tributário e sucessório
  { value: 'statement_type', label: 'Tipo de declaração de IR' },
  { value: 'pgbl_possibility', label: 'Possibilidade de PGBL' },
  { value: 'pgbl_value', label: 'Valor do PGBL' },
  { value: 'pgbl_savings', label: 'Economia com PGBL' },
  { value: 'marital_status', label: 'Estado civil' },
  { value: 'patrimony_value', label: 'Valor do patrimônio' },
  { value: 'inventory_cost', label: 'Custo de inventário' },
  // Passo 9 — Implementação
  { value: 'implementation_recommendation', label: 'Recomendação de implementação' },
]

const fieldTypeLabel = (value) => {
  const option = FIELD_TYPE_OPTIONS.find((item) => item.value === value)
  return option ? option.label : value
}

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BPagination,
    vSelect,
    flatPickr,
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      loadingConsultants: false,
      loadingFranchises: false,
      filters: {
        step: null,
        field_key: null,
        pn_consultant_id: [],
        digital_franchise_id: [],
      },
      rangeDate: undefined,
      flatPickrConfig: {
        altFormat: 'j M Y',
        altInput: true,
        mode: 'range',
        dateFormat: 'Y-m-d',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      totals: {},
      rates: {
        strict_acceptance_rate: 0,
        edited_acceptance_rate: 0,
        general_acceptance_rate: 0,
        rejection_rate: 0,
      },
      avgProcessingMs: null,
      auditItems: [],
      auditMeta: { current_page: 1, per_page: 10, total: 0, from: 0, to: 0 },
      stepOptions: [
        { key: null, name: 'Todos' },
        { key: 1, name: 'Passo 1' },
        { key: 2, name: 'Passo 2' },
        { key: 3, name: 'Passo 3' },
        { key: 4, name: 'Passo 4' },
        { key: 5, name: 'Passo 5' },
        { key: 7, name: 'Passo 7' },
        { key: 9, name: 'Passo 9' },
      ],
      typeOptions: [
        { value: null, label: 'Todos' },
        ...FIELD_TYPE_OPTIONS,
      ],
      auditFields: [
        { key: 'id', label: '#' },
        { key: 'created_at', label: 'Data', formatter: v => (v ? new Date(v).toLocaleString('pt-BR') : '-') },
        { key: 'customer.name', label: 'Cliente' },
        { key: 'user.name', label: 'PN' },
        { key: 'step', label: 'Passo' },
        { key: 'field_key', label: 'Tipo', formatter: v => fieldTypeLabel(v) },
        { key: 'status', label: 'Status' },
        { key: 'processing_time_ms', label: 'Tempo', formatter: v => (v ? `${(v / 1000).toFixed(1)}s` : '-') },
      ],
    }
  },
  computed: {
    ...mapGetters({
      consultantList: sharedTypes.CONSULTANTS,
      franchiseList: sharedTypes.FRANCHISES,
    }),
    totalizerCards() {
      return [
        { key: 'generated', label: 'Geradas', value: this.totals.generated || 0 },
        { key: 'accepted', label: 'Aceitas', value: this.totals.accepted || 0 },
        { key: 'accepted_edited', label: 'Aceitas c/ edição', value: this.totals.accepted_edited || 0 },
        { key: 'rejected', label: 'Rejeitadas', value: this.totals.rejected || 0 },
        { key: 'presented', label: 'Apresentadas', value: this.totals.presented_pending || 0 },
        { key: 'errored', label: 'Com erro', value: this.totals.errored || 0 },
      ]
    },
    avgProcessingLabel() {
      if (!this.avgProcessingMs) return '-'
      return `${(this.avgProcessingMs / 1000).toFixed(1)}s`
    },
  },
  mounted() {
    this.loadConsultants()
    this.loadFranchises()
    this.loadAll()
  },
  methods: {
    ...mapActions({
      getIndicators: types.GET_COPILOT_INDICATORS,
      getAudit: types.GET_COPILOT_AUDIT,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getFranchises: sharedTypes.GET_FRANCHISES,
    }),
    loadConsultants() {
      this.loadingConsultants = true
      this.getConsultants()
        .catch(() => this.notifyError('Não foi possível carregar os parceiros de negócio.'))
        .finally(() => {
          this.loadingConsultants = false
        })
    },
    loadFranchises() {
      this.loadingFranchises = true
      this.getFranchises()
        .catch(() => this.notifyError('Não foi possível carregar as franquias digitais.'))
        .finally(() => {
          this.loadingFranchises = false
        })
    },
    activeFilters() {
      const f = {}
      Object.keys(this.filters).forEach(k => {
        const value = this.filters[k]
        if (value === undefined || value === null || value === '') return
        if (Array.isArray(value) && value.length === 0) return
        f[k] = value
      })
      Object.assign(f, this.parseDateRange())
      return f
    },
    parseDateRange() {
      if (!this.rangeDate) return {}
      const parts = this.rangeDate.split(' até ')
      const start = (parts[0] || '').trim()
      // Se o usuário escolheu só uma data, usa a mesma como início e fim.
      const end = (parts[1] || parts[0] || '').trim()
      const out = {}
      if (start) out.start_date = start
      if (end) out.end_date = end
      return out
    },
    loadAll() {
      this.auditMeta.current_page = 1
      this.loadIndicators()
      this.loadAudit()
    },
    clearFilters() {
      this.filters.step = null
      this.filters.field_key = null
      this.filters.pn_consultant_id = []
      this.filters.digital_franchise_id = []
      this.rangeDate = undefined
      this.loadAll()
    },
    loadIndicators() {
      this.loading = true
      this.getIndicators(this.activeFilters())
        .then(resp => {
          this.totals = resp.data.totals || {}
          this.rates = resp.data.rates || this.rates
          this.avgProcessingMs = resp.data.avg_processing_time_ms
        })
        .catch(() => this.notifyError())
        .finally(() => {
          this.loading = false
        })
    },
    loadAudit() {
      const params = {
        ...this.activeFilters(),
        page: this.auditMeta.current_page,
        per_page: this.auditMeta.per_page,
      }
      this.getAudit(params)
        .then(resp => {
          const { data } = resp
          this.auditItems = data.data || []
          this.auditMeta = {
            current_page: data.current_page || 1,
            per_page: data.per_page || 10,
            total: data.total || 0,
            from: data.from || 0,
            to: data.to || 0,
          }
        })
        .catch(() => this.notifyError())
    },
    onPageChange(page) {
      this.auditMeta.current_page = page
      this.loadAudit()
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    effectiveStatus(recommendation) {
      return recommendation.technical_status === 'error'
        ? 'processing_error'
        : recommendation.status
    },
    statusVariant(status) {
      const map = {
        processing: 'light-warning',
        accepted: 'success',
        accepted_edited: 'info',
        rejected: 'danger',
        regenerated: 'warning',
        processing_error: 'dark',
        presented: 'light-primary',
      }
      return map[status] || 'secondary'
    },
    notifyError(text = 'Não foi possível carregar a auditoria do Copiloto.') {
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Oops!',
          text,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.totalizer-card h3 {
  font-weight: 700;
}

/* Distribui os cards de taxa igualmente numa linha só (sem sobrar espaço à direita),
   quebrando de forma responsiva em telas menores. */
.rate-col {
  flex: 1 1 200px;
}
</style>

<style lang="scss">
$themeColor: #f26237;
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
